import { CircleInfoIcon, PenIcon } from "@xola/icons";
import { Checkbox, FormGroup, Label, SubmitButton, Tooltip } from "@xola/ui-kit";
import { SwitchField } from "common";
import { isNil, omit } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import {
    AndFilter,
    BaseSchedule,
    BaseScheduleType,
    COUPON_ROLES,
    COUPON_ROLES_COPY,
    CouponRole,
    CouponScope,
    EqualsFilter,
    InFilter,
    InScheduleFilter,
    Permission,
    Privacy,
    ProductObject,
    PurchaseRule,
    SelectionState,
    UserRole,
    ValidationLevel,
} from "types";
import { useSeller } from "../../hooks/useAccount";
import { useAuthorization } from "../../hooks/useAuthorization";
import { createAndFilter, createEqualsFilter, createInFilter, createInScheduleFilter } from "../../models/RuleFilter";
import { ConfirmLeaveModal } from "../Common/ConfirmLeaveModal";
import { ProductOrCatalogSelectorModal } from "../Common/ProductOrCatalogSelectorModal";
import { ProductSelectorModal } from "../Common/ProductSelectorModal";
import { SecondaryButton } from "../Common/SecondaryButton";
import { SelectedProductList } from "../Common/SelectedProductList";
import { ContentLayout } from "../Layout/ContentLayout";
import { CouponEditorProps } from "./CouponEditorProps";
import { CouponScheduleEditor } from "./CouponScheduleEditor";

export const CouponRestrictionForm = ({ coupon, isSaving, onSubmit, onCancel }: CouponEditorProps) => {
    const seller = useSeller();
    const { isGranted } = useAuthorization();

    const initialPurchaseRule: PurchaseRule = coupon.purchaseRule ?? {
        name: "",
        actions: [],
        seller: { id: seller.id },
    };

    const [formTouched, setFormTouched] = useState(false);
    const [purchaseRuleState, setPurchaseRuleState] = useState(initialPurchaseRule);

    const [productSelectorModalOpen, setProductSelectorModalOpen] = useState(false);
    const [productRestrictions, setProductRestrictions] = useState<SelectionState>(() => {
        // Extract product IDs from purchaseRule.filter if it has "product.id" in_filter
        const selectedProductIds = getSelectedProductIdsFromPurchaseRule(coupon.purchaseRule);

        if (selectedProductIds && selectedProductIds.length > 0) {
            return {
                all: false,
                selected: selectedProductIds,
            };
        }

        return {
            all: true,
            selected: [],
        };
    });

    // State for permissions - null means all roles allowed, array with >= 1 roles means specific roles are allowed, empty means only seller is allowed to apply the coupon
    const [permissions, setPermissions] = useState<CouponRole[] | null>(coupon.permissions ?? null);

    useEffect(() => {
        if (coupon.purchaseRule) {
            setPurchaseRuleState(coupon.purchaseRule);
        }
    }, [coupon.purchaseRule, setPurchaseRuleState]);

    const isMerchandiseAvailable = isGranted(Permission.MERCHANDISE_MODIFY);

    const handleSaveClick = () => {
        setFormTouched(false);

        onSubmit({
            coupon: {
                ...omit(coupon, ["experiences", "merchandise"]),
                purchaseRule: purchaseRuleState,
                permissions,
            },
        });
    };

    const handleCancelClick = () => {
        if (coupon.id) {
            setPurchaseRuleState(initialPurchaseRule);
            setPermissions(coupon.permissions ?? null);
        }

        setFormTouched(false);
        onCancel();
    };

    // Handle role permission changes
    const handlePermissionChange = useCallback(
        (role: CouponRole, isChecked: boolean) => {
            // Treat both null and undefined as unrestricted (all roles allowed)
            const isUnrestricted = isNil(permissions);
            const currentPermissions = isUnrestricted ? [...COUPON_ROLES] : [...permissions];
            let updatedPermissions: CouponRole[] = [];

            if (isChecked) {
                updatedPermissions = currentPermissions.includes(role)
                    ? currentPermissions
                    : [...currentPermissions, role];
            } else {
                // If permissions is null/undefined and a role is unchecked, we add all other roles to permissions
                // If permissions is already an array, we filter out the unchecked role
                updatedPermissions = currentPermissions.filter((permissionRole) => permissionRole !== role);
            }

            // If all roles are selected, set permissions to null (unrestricted); otherwise, set the array
            setPermissions(updatedPermissions.length === COUPON_ROLES.length ? null : updatedPermissions);
            setFormTouched(true);
        },
        [permissions],
    );

    const handlePermissionSwitchChange = useCallback(
        (role: CouponRole) => (isChecked: boolean) => {
            handlePermissionChange(role, isChecked);
        },
        [handlePermissionChange],
    );

    // Check if a role is currently enabled
    const isRoleEnabled = useCallback(
        (role: CouponRole): boolean => isNil(permissions) || permissions.includes(role),
        [permissions],
    );

    const updatePurchaseRule = (values: Partial<PurchaseRule>) => {
        setPurchaseRuleState({ ...purchaseRuleState, ...values });
    };

    const handlePurchaseWindowScheduleChange = (schedule: BaseSchedule | null) => {
        const filter = getPurchaseRuleFilter(
            schedule,
            purchaseBlackoutSchedules,
            arrivalWindowSchedule,
            arrivalBlackoutSchedules,
            privacy,
            productRestrictions.selected,
        );

        updatePurchaseRule({ filter });
        setFormTouched(true);
    };

    const handlePurchaseBlackoutSchedulesChange = (schedules: BaseSchedule[]) => {
        const filter = getPurchaseRuleFilter(
            purchaseWindowSchedule,
            schedules,
            arrivalWindowSchedule,
            arrivalBlackoutSchedules,
            privacy,
            productRestrictions.selected,
        );

        updatePurchaseRule({ filter });
        setFormTouched(true);
    };

    const handleArrivalWindowScheduleChange = (schedule: BaseSchedule | null) => {
        const filter = getPurchaseRuleFilter(
            purchaseWindowSchedule,
            purchaseBlackoutSchedules,
            schedule,
            arrivalBlackoutSchedules,
            privacy,
            productRestrictions.selected,
        );

        updatePurchaseRule({ filter });
        setFormTouched(true);
    };

    const handleArrivalBlackoutSchedulesChange = (schedules: BaseSchedule[]) => {
        const filter = getPurchaseRuleFilter(
            purchaseWindowSchedule,
            purchaseBlackoutSchedules,
            arrivalWindowSchedule,
            schedules,
            privacy,
            productRestrictions.selected,
        );

        updatePurchaseRule({ filter });
        setFormTouched(true);
    };

    const handlePrivateCheckboxChange = (e: any) => {
        const updatedPrivacy = e.target.checked && privacy === Privacy.PUBLIC ? null : Privacy.PUBLIC;

        const filter = getPurchaseRuleFilter(
            purchaseWindowSchedule,
            purchaseBlackoutSchedules,
            arrivalWindowSchedule,
            arrivalBlackoutSchedules,
            updatedPrivacy,
            productRestrictions.selected,
        );

        updatePurchaseRule({ filter });
        setFormTouched(true);
    };

    const handleSharedCheckboxChange = (e: any) => {
        const updatedPrivacy = e.target.checked && privacy === Privacy.PRIVATE ? null : Privacy.PRIVATE;

        const filter = getPurchaseRuleFilter(
            purchaseWindowSchedule,
            purchaseBlackoutSchedules,
            arrivalWindowSchedule,
            arrivalBlackoutSchedules,
            updatedPrivacy,
            productRestrictions.selected,
        );

        updatePurchaseRule({ filter });
        setFormTouched(true);
    };

    const purchaseWindowSchedule = getWindowSchedule(purchaseRuleState, "currentTime");
    const purchaseBlackoutSchedules = getBlackoutSchedules(purchaseRuleState, "currentTime");
    const arrivalWindowSchedule = getWindowSchedule(purchaseRuleState, "arrivalDateTime");
    const arrivalBlackoutSchedules = getBlackoutSchedules(purchaseRuleState, "arrivalDateTime");

    const privacy = getPrivacy(purchaseRuleState);

    const handleProductRestrictionsChange = (productRestrictions: SelectionState) => {
        const filter = getPurchaseRuleFilter(
            purchaseWindowSchedule,
            purchaseBlackoutSchedules,
            arrivalWindowSchedule,
            arrivalBlackoutSchedules,
            privacy,
            productRestrictions.selected,
        );

        updatePurchaseRule({ filter });
        setProductRestrictions(productRestrictions);
        setFormTouched(true);
    };

    // Determine if this is a merchandise coupon
    // Check scope in purchaseRule for both saved and newly created coupons
    const shortcutOptions = coupon.purchaseRule?.actions?.[0]?.shortcut?.options;
    const isMerchandiseCoupon =
        (shortcutOptions && "scope" in shortcutOptions && shortcutOptions.scope === CouponScope.PER_MERCHANDISE) ||
        (shortcutOptions &&
            "templateTypes" in shortcutOptions &&
            Array.isArray(shortcutOptions.templateTypes) &&
            shortcutOptions.templateTypes.length === 1 &&
            (shortcutOptions.templateTypes as string[])[0] === "ware");
    return (
        <ContentLayout isWide className="space-y-6" isDisabled={!isGranted(Permission.MODIFY_COUPONS) || isSaving}>
            <ConfirmLeaveModal isFormTouched={formTouched} />
            <ContentLayout.Section
                title="Purchase Window"
                description="Limits the dates within which this code can be applied to a purchase"
            >
                <CouponScheduleEditor
                    title="Purchase Window"
                    description="Define the window of time within which a purchase must be made in order to qualify for the discount. Note, that this is independent of the date of the event when the service will be provided."
                    windowSchedule={purchaseWindowSchedule}
                    blackoutSchedules={purchaseBlackoutSchedules}
                    onWindowScheduleChange={handlePurchaseWindowScheduleChange}
                    onBlackoutSchedulesChange={handlePurchaseBlackoutSchedulesChange}
                />
            </ContentLayout.Section>

            <ContentLayout.Section
                title="Arrival Window"
                description="Limits the start date and start time of the events that this code can be applied to"
            >
                <CouponScheduleEditor
                    title="Arrival Window"
                    description="Define the window of time within which the event will take place and the services provided."
                    windowSchedule={arrivalWindowSchedule}
                    blackoutSchedules={arrivalBlackoutSchedules}
                    onWindowScheduleChange={handleArrivalWindowScheduleChange}
                    onBlackoutSchedulesChange={handleArrivalBlackoutSchedulesChange}
                />
            </ContentLayout.Section>

            <ContentLayout.Section
                title="Applies To"
                description="Select if you would like this coupon to be applicable to all products, or select individual ones"
            >
                <FormGroup className="space-y-2">
                    {/* TODO: Update to use SelectedProductListInput */}
                    <div className="flex items-end">
                        <Label className="flex-grow">Products</Label>
                        <SecondaryButton size="small" onClick={() => setProductSelectorModalOpen(true)}>
                            <PenIcon />
                        </SecondaryButton>
                    </div>
                    {isMerchandiseCoupon || isMerchandiseAvailable ? (
                        <ProductOrCatalogSelectorModal
                            isOpen={productSelectorModalOpen}
                            value={productRestrictions}
                            productObjects={[ProductObject.EXPERIENCE, ProductObject.WARE]}
                            defaultEnabledProductObjects={[ProductObject.EXPERIENCE, ProductObject.WARE]}
                            title="Select Products"
                            onClose={() => setProductSelectorModalOpen(false)}
                            onConfirm={handleProductRestrictionsChange}
                        />
                    ) : (
                        <ProductSelectorModal
                            isOpen={productSelectorModalOpen}
                            value={productRestrictions}
                            objects={[ProductObject.EXPERIENCE]}
                            onClose={() => setProductSelectorModalOpen(false)}
                            onConfirm={handleProductRestrictionsChange}
                        />
                    )}
                    <SelectedProductList selectedProducts={productRestrictions ?? { all: true, experiences: [] }} />
                </FormGroup>

                <FormGroup>
                    <Label>
                        Pricing Type
                        <Tooltip
                            placement="auto"
                            className="ml-1"
                            content="Determine what types of booking this coupon can be applied to. You can offer a private option for of your experiences by configuring private pricing in Listings > Pricing > Private Pricing"
                        >
                            <CircleInfoIcon />
                        </Tooltip>
                    </Label>

                    <div className="flex space-x-4">
                        <Checkbox
                            label="Private Events"
                            data-testid="coupon-restrictions-form-private-events"
                            checked={privacy === null || privacy === Privacy.PRIVATE}
                            onChange={handlePrivateCheckboxChange}
                        />

                        <Checkbox
                            label="Shared Events"
                            data-testid="coupon-restrictions-form-shared-events"
                            checked={privacy === null || privacy === Privacy.PUBLIC}
                            onChange={handleSharedCheckboxChange}
                        />
                    </div>
                </FormGroup>
            </ContentLayout.Section>

            <ContentLayout.Section title="Roles" description="Limit coupon usage based on the user's role">
                <FormGroup>
                    <Label>Role Permissions</Label>
                    {/* This is a display only toggle to indicate that the seller / account owner can always apply the coupon */}
                    <div key={UserRole.SELLER}>
                        <SwitchField isChecked isDisabled>
                            Account Owner
                        </SwitchField>
                    </div>

                    {COUPON_ROLES.map((role) => (
                        <div key={role}>
                            <SwitchField isChecked={isRoleEnabled(role)} onChange={handlePermissionSwitchChange(role)}>
                                {COUPON_ROLES_COPY[role]}
                            </SwitchField>
                        </div>
                    ))}
                </FormGroup>
            </ContentLayout.Section>

            <ContentLayout.Footer>
                <SubmitButton type="submit" isLoading={isSaving} onClick={handleSaveClick}>
                    {coupon.id ? "Save" : "Next"}
                </SubmitButton>

                <SecondaryButton type="reset" className="ml-3" onClick={handleCancelClick}>
                    {coupon.id ? "Cancel" : "Back"}
                </SecondaryButton>
            </ContentLayout.Footer>
        </ContentLayout>
    );
};

// Find the window schedule from the purchase rule.
export const getWindowSchedule = (purchaseRule: PurchaseRule, field: string): BaseSchedule | null => {
    if (!purchaseRule) {
        // Till all coupons are migrated you may have a missing purchase rule
        return null;
    }

    if (purchaseRule.filter?.object !== "and_filter") {
        return null;
    }

    const inScheduleFilters = purchaseRule.filter?.filters.filter((filter) => {
        return filter.object === "in_schedule_filter";
    }) as InScheduleFilter[];

    const filter = inScheduleFilters.find((filter) => {
        return filter.schedules.some((schedule) => {
            return schedule.type === BaseScheduleType.AVAILABLE && filter.field === field;
        });
    });

    return (
        filter?.schedules.find((schedule) => {
            return schedule.type === BaseScheduleType.AVAILABLE;
        }) ?? null
    );
};

// Find the array of blackout schedules from the purchase rule.
export const getBlackoutSchedules = (purchaseRule: PurchaseRule, field: string): BaseSchedule[] => {
    if (!purchaseRule) {
        // Till all coupons are migrated you may have a null purchase rule
        return [];
    }

    if (purchaseRule.filter?.object !== "and_filter") {
        return [];
    }

    const inScheduleFilters = purchaseRule.filter?.filters.filter((filter) => {
        return filter.object === "in_schedule_filter";
    }) as InScheduleFilter[];

    const filter = inScheduleFilters.find((filter) => {
        return filter.schedules.some((schedule) => {
            return schedule.type === BaseScheduleType.UNAVAILABLE && filter.field === field;
        });
    });

    return filter?.schedules ?? [];
};

const getPrivacy = (purchaseRule: PurchaseRule): Privacy | null => {
    // Till all coupons are migrated you may have a null purchase rule
    if (!purchaseRule) {
        return null;
    }

    if (purchaseRule.filter?.object !== "and_filter") {
        return null;
    }

    const equalsFilters = purchaseRule.filter?.filters.filter((filter) => {
        return filter.object === "equals_filter";
    }) as EqualsFilter[];

    const filter = equalsFilters.find((filter) => {
        return filter.field === "privacy";
    });

    return (filter?.operand as Privacy) ?? null;
};

// Create a purchase rule object from the provided schedules.
const getPurchaseRuleFilter = (
    purchaseWindowSchedule: BaseSchedule | null = null,
    purchaseBlackoutSchedules: BaseSchedule[] = [],
    arrivalWindowSchedule: BaseSchedule | null = null,
    arrivalBlackoutSchedules: BaseSchedule[] = [],
    privacy: Privacy | null = null,
    products: ID[] = [],
): AndFilter | null => {
    const filters: Array<InScheduleFilter | InFilter | EqualsFilter> = [];

    if (purchaseWindowSchedule) {
        filters.push(
            createInScheduleFilter({
                schedules: [purchaseWindowSchedule],
                field: "currentTime",
                validationLevel: ValidationLevel.ERROR,
            }),
        );
    }

    if (purchaseBlackoutSchedules.length > 0) {
        filters.push(
            createInScheduleFilter({
                schedules: purchaseBlackoutSchedules,
                field: "currentTime",
                validationLevel: ValidationLevel.ERROR,
            }),
        );
    }

    if (arrivalWindowSchedule) {
        filters.push(
            createInScheduleFilter({
                schedules: [arrivalWindowSchedule],
                field: "arrivalDateTime",
                validationLevel: ValidationLevel.ERROR,
            }),
        );
    }

    if (arrivalBlackoutSchedules.length > 0) {
        filters.push(
            createInScheduleFilter({
                schedules: arrivalBlackoutSchedules,
                field: "arrivalDateTime",
                validationLevel: ValidationLevel.ERROR,
            }),
        );
    }

    if (privacy) {
        filters.push(createEqualsFilter(privacy, "privacy", ValidationLevel.ERROR));
    }

    if (products.length > 0) {
        filters.push(
            createInFilter({ operand: products, field: "product.id", validationLevel: ValidationLevel.ERROR }),
        );
    }

    return filters.length > 0 ? createAndFilter({ filters }) : null;
};

// Extract selected product IDs from purchaseRule.filter
const getSelectedProductIdsFromPurchaseRule = (purchaseRule?: PurchaseRule | null): ID[] | null => {
    if (!purchaseRule?.filter) {
        return null;
    }

    const { filter } = purchaseRule;

    // Check if top-level filter is and_filter
    if (filter.object === "and_filter" && filter.filters) {
        for (const filters of filter.filters) {
            if (filters.object === "in_filter" && filters.field === "product.id" && Array.isArray(filters.operand)) {
                return filters.operand as ID[];
            }
        }
    }

    // Check if top-level filter itself is in_filter with product.id
    if (filter.object === "in_filter" && filter.field === "product.id" && Array.isArray(filter.operand)) {
        return filter.operand as ID[];
    }

    return null;
};
